import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, Star, Package } from "lucide-react";
import { useAdminProducts } from "@/lib/adminProductsStore";
import { cn } from "@/lib/utils";

const badgeColors: Record<string, string> = {
  SALE: "bg-red-100 text-red-700",
  NEW: "bg-stone-100 text-stone-700",
  "BEST SELLER": "bg-emerald-50 text-emerald-700",
};

export default function AdminProducts() {
  const { products, deleteProduct } = useAdminProducts();
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const categories = [...new Set(products.map((p) => p.category))];

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
    const matchCat = !filterCat || p.category === filterCat;
    return matchSearch && matchCat;
  });

  function confirmDelete(id: string) {
    deleteProduct(id);
    setDeletingId(null);
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Prodotti</h1>
          <p className="text-stone-500 text-sm mt-1">{products.length} articoli nel catalogo</p>
        </div>
        <NavLink
          to="/admin/products/new"
          className="flex items-center gap-2 bg-stone-900 text-white text-xs tracking-widest uppercase px-5 py-3 hover:bg-stone-700 transition-colors"
        >
          <Plus className="size-4" /> Nuovo prodotto
        </NavLink>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 flex flex-wrap gap-3 mb-6 border border-stone-200">
        <div className="flex items-center gap-2 border border-stone-200 px-3 flex-1 min-w-48">
          <Search className="size-4 text-stone-400 shrink-0" />
          <input
            type="text"
            placeholder="Cerca per nome o categoria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 py-2 text-sm focus:outline-none text-stone-700 placeholder-stone-400"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="border border-stone-200 px-3 py-2 text-sm text-stone-600 focus:outline-none bg-white"
        >
          <option value="">Tutte le categorie</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Package className="size-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-400 text-sm">Nessun prodotto trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-5 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">Prodotto</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">Categoria</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">Prezzo</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">Badge</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">Rating</th>
                  <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">Stock</th>
                  <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-stone-500 font-medium">In evidenza</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((product) => (
                  <tr key={product.id} className="hover:bg-stone-50 transition-colors group">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="size-12 object-cover bg-stone-100 shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-stone-900 truncate max-w-48">{product.name}</p>
                          <p className="text-xs text-stone-400 truncate max-w-48">{product.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-stone-600">{product.category}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-semibold text-stone-900">€{product.price.toLocaleString("it-IT")}</span>
                        {product.originalPrice && (
                          <span className="text-xs text-stone-400 line-through ml-2">€{product.originalPrice.toLocaleString("it-IT")}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {product.badge ? (
                        <span className={cn("text-[10px] font-semibold px-2 py-1 tracking-wider uppercase", badgeColors[product.badge])}>
                          {product.badge}
                        </span>
                      ) : (
                        <span className="text-stone-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Star className="size-3 fill-amber-400 text-amber-400" />
                        <span className="text-stone-600 text-xs">{product.rating}</span>
                        <span className="text-stone-400 text-xs">({product.reviews})</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn("size-2 rounded-full inline-block", product.inStock ? "bg-green-500" : "bg-red-400")} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {product.featured ? (
                        <span className="text-emerald-500 text-xs">★</span>
                      ) : (
                        <span className="text-stone-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <NavLink
                          to={`/admin/products/${product.id}/edit`}
                          className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                          title="Modifica"
                        >
                          <Pencil className="size-4" />
                        </NavLink>
                        {deletingId === product.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => confirmDelete(product.id)}
                              className="px-2 py-1 bg-red-500 text-white text-xs hover:bg-red-600 transition-colors"
                            >
                              Conferma
                            </button>
                            <button
                              onClick={() => setDeletingId(null)}
                              className="px-2 py-1 bg-stone-200 text-stone-700 text-xs hover:bg-stone-300 transition-colors"
                            >
                              Annulla
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingId(product.id)}
                            className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-stone-400 text-xs mt-4 text-right">
          {filtered.length} di {products.length} prodotti
        </p>
      )}
    </div>
  );
}
